// utils/chordParser.ts
// Chord parsing utilities for converting chord symbols to musical data

import { NOTES, SCALE_FORMULAS } from './constants';
import type { Note, Scale } from '../presets/presetSchema';

// ================================================================================
// TYPES AND INTERFACES
// ================================================================================

export interface ChordParseResult {
  readonly chords: ParsedChord[];
  readonly errors: string[];
}

export interface ParsedChord {
  readonly root: Note;
  readonly quality: Scale;
  readonly formula: readonly number[];
  readonly originalInput: string;
}

export interface ChordValidationResult {
  readonly isValid: boolean;
  readonly chord: ParsedChord | null;
  readonly error: string | null;
}

export interface ExampleProgression {
  readonly name: string;
  readonly chords: string;
}

// ================================================================================
// CHORD QUALITY MAPPINGS
// ================================================================================

// Chord quality abbreviations mapped to SCALE_FORMULAS
// Only includes chord types that exist in SCALE_FORMULAS
type ChordScale = 'MAJOR_TRIAD' | 'MINOR_TRIAD' | 'DOMINANT_7TH' | 'MAJOR_7TH' | 'MINOR_7TH' | 
                  'DIMINISHED_TRIAD' | 'DIMINISHED_7TH' | 'AUGMENTED_TRIAD' | 'HALF_DIMINISHED_7TH' | 
                  'MINOR_MAJOR_7TH' | 'AUGMENTED_7TH' | 'AUGMENTED_MAJOR_7TH';

const CHORD_QUALITY_ABBREVIATIONS: Record<ChordScale, readonly number[]> = {
  'MAJOR_TRIAD': SCALE_FORMULAS.MAJOR_TRIAD,
  'MINOR_TRIAD': SCALE_FORMULAS.MINOR_TRIAD,
  'DOMINANT_7TH': SCALE_FORMULAS.DOMINANT_7TH,
  'MAJOR_7TH': SCALE_FORMULAS.MAJOR_7TH,
  'MINOR_7TH': SCALE_FORMULAS.MINOR_7TH,
  'DIMINISHED_TRIAD': SCALE_FORMULAS.DIMINISHED_TRIAD,
  'DIMINISHED_7TH': SCALE_FORMULAS.DIMINISHED_7TH,
  'AUGMENTED_TRIAD': SCALE_FORMULAS.AUGMENTED_TRIAD,
  'HALF_DIMINISHED_7TH': SCALE_FORMULAS.HALF_DIMINISHED_7TH,
  'MINOR_MAJOR_7TH': SCALE_FORMULAS.MINOR_MAJOR_7TH,
  'AUGMENTED_7TH': SCALE_FORMULAS.AUGMENTED_7TH,
  'AUGMENTED_MAJOR_7TH': SCALE_FORMULAS.AUGMENTED_MAJOR_7TH
} as const;

// Quality abbreviations mapping
const QUALITY_ABBREVIATIONS: Record<ChordScale, readonly string[]> = {
  'MAJOR_TRIAD': ['', 'maj', 'major', 'M'],
  'MINOR_TRIAD': ['m', 'min', 'minor', '-'],
  'DOMINANT_7TH': ['7', 'dom7'],
  'MAJOR_7TH': ['maj7', 'M7', 'ma7', 'major7'],
  'MINOR_7TH': ['m7', 'min7', 'minor7', '-7'],
  'DIMINISHED_TRIAD': ['dim', '°', 'o'],
  'DIMINISHED_7TH': ['dim7', '°7', 'o7'],
  'AUGMENTED_TRIAD': ['aug', '+', '#5'],
  'HALF_DIMINISHED_7TH': ['m7b5', 'ø7', 'ø', 'min7b5', 'half-dim7'],
  'MINOR_MAJOR_7TH': ['mM7', 'minmaj7', 'mmaj7', 'm(maj7)'],
  'AUGMENTED_7TH': ['aug7', '+7', '7#5'],
  'AUGMENTED_MAJOR_7TH': ['augmaj7', '+maj7', 'maj7#5']
} as const;

// ================================================================================
// ENHARMONIC EQUIVALENTS
// ================================================================================

const ENHARMONIC_EQUIVALENTS: Record<string, string> = {
  'C#': 'Db', 'Db': 'C#',
  'D#': 'Eb', 'Eb': 'D#',
  'F#': 'Gb', 'Gb': 'F#',
  'G#': 'Ab', 'Ab': 'G#',
  'A#': 'Bb', 'Bb': 'A#'
} as const;

// ================================================================================
// PARSING FUNCTIONS
// ================================================================================

/**
 * Parse a chord progression string into an array of chord objects
 * @param input - Chord progression string (e.g., "C Am F G")
 * @returns Object containing parsed chords and any errors
 */
export function parseChordProgression(input: string): ChordParseResult {
  if (!input || typeof input !== 'string') {
    return { chords: [], errors: ['Please enter a chord progression'] };
  }

  // Clean and split the input
  const cleanInput = normalizeChordInput(input);
  const chordStrings = cleanInput.split(/[\s,|]+/).filter(chord => chord.length > 0);
  
  if (chordStrings.length === 0) {
    return { chords: [], errors: ['Please enter at least one chord'] };
  }

  const results = chordStrings.map(chordString => parseChord(chordString));
  const validChords = results.filter(result => result.isValid).map(result => result.chord!);
  const errors = results.filter(result => !result.isValid).map(result => result.error!);

  return {
    chords: validChords,
    errors: errors
  };
}

/**
 * Parse an individual chord string
 * @param chordString - Individual chord (e.g., "Cm", "G7", "Amaj7")
 * @returns Validation result with parsed chord or error
 */
export function parseChord(chordString: string): ChordValidationResult {
  if (!chordString || typeof chordString !== 'string') {
    return { isValid: false, chord: null, error: 'Invalid chord input' };
  }

  const normalizedChord = chordString.trim();
  
  // Regex to parse chord components
  // Matches: root note (C, D#, Bb) + optional quality (m, 7, maj7, etc.)
  const chordRegex = /^([A-G][#b]?)(.*)?$/i;
  const match = normalizedChord.match(chordRegex);

  if (!match) {
    return { 
      isValid: false, 
      chord: null, 
      error: `Invalid chord format: "${chordString}"` 
    };
  }

  const [, rootNote, qualityPart = ''] = match;
  
  // Validate and normalize root note
  const normalizedRoot = normalizeNote(rootNote);
  if (!normalizedRoot) {
    return { 
      isValid: false, 
      chord: null, 
      error: `Invalid root note: "${rootNote}"` 
    };
  }

  // Parse and validate chord quality
  const quality = parseChordQuality(qualityPart);
  if (!quality) {
    return { 
      isValid: false, 
      chord: null, 
      error: `Unsupported chord quality: "${qualityPart}" in "${chordString}"` 
    };
  }

  return {
    isValid: true,
    chord: {
      root: normalizedRoot,
      quality: quality.name,
      formula: quality.formula,
      originalInput: chordString
    },
    error: null
  };
}

// ================================================================================
// HELPER FUNCTIONS
// ================================================================================

/**
 * Parse chord quality from the quality part of a chord
 * @param qualityPart - The quality part (e.g., "m", "7", "maj7")
 * @returns Quality object with name and formula, or null if invalid
 */
function parseChordQuality(qualityPart: string): { name: Scale; formula: readonly number[] } | null {
  const cleanQuality = qualityPart.toLowerCase().trim();
  
  // Handle empty quality (major chord)
  if (cleanQuality === '') {
    return {
      name: 'MAJOR_TRIAD',
      formula: CHORD_QUALITY_ABBREVIATIONS['MAJOR_TRIAD']
    };
  }

  // Check direct matches in QUALITY_ABBREVIATIONS
  for (const [qualityName, abbreviations] of Object.entries(QUALITY_ABBREVIATIONS)) {
    if (abbreviations.includes(cleanQuality)) {
      const chordScale = qualityName as ChordScale;
      const formula = CHORD_QUALITY_ABBREVIATIONS[chordScale];
      if (formula) {
        return {
          name: chordScale as Scale, // ChordScale is a subset of Scale
          formula: formula
        };
      }
    }
  }

  return null;
}

/**
 * Normalize note name to standard format
 * @param note - Note name (e.g., "C", "c#", "Bb")
 * @returns Normalized note name or null if invalid
 */
function normalizeNote(note: string): Note | null {
  if (!note || typeof note !== 'string') return null;
  
  const cleanNote = note.trim();
  const noteRegex = /^([A-G])[#b]?$/i;
  const match = cleanNote.match(noteRegex);
  
  if (!match) return null;
  
  // Capitalize the note letter
  const normalized = cleanNote.charAt(0).toUpperCase() + cleanNote.slice(1).toLowerCase();
  
  // Check if it's a valid note in our NOTES array
  if (NOTES.includes(normalized as Note)) {
    return normalized as Note;
  }
  
  // Convert enharmonic equivalents to our standard sharp notation
  if (normalized in ENHARMONIC_EQUIVALENTS) {
    const equivalent = ENHARMONIC_EQUIVALENTS[normalized];
    if (NOTES.includes(equivalent as Note)) {
      return equivalent as Note;
    }
  }
  
  return null;
}

/**
 * Normalize and clean chord progression input
 * @param input - Raw input string
 * @returns Cleaned input string
 */
function normalizeChordInput(input: string): string {
  return input
    .trim()
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .replace(/[,|]+/g, ' ') // Replace commas and pipes with spaces
    .replace(/\s+/g, ' '); // Clean up any remaining multiple spaces
}

// ================================================================================
// VALIDATION AND UTILITY FUNCTIONS
// ================================================================================

/**
 * Validate if a chord is supported by the app
 * @param root - Root note
 * @param quality - Chord quality
 * @returns True if chord is supported
 */
export function validateChord(root: string, quality: string): boolean {
  const normalizedRoot = normalizeNote(root);
  if (!normalizedRoot) return false;
  
  return quality in CHORD_QUALITY_ABBREVIATIONS;
}

/**
 * Get example chord progressions for user reference
 * @returns Array of example progression objects
 */
export function getExampleProgressions(): readonly ExampleProgression[] {
  return [
    { name: 'Pop Progression', chords: 'C Am F G' },
    { name: 'Jazz ii-V-I', chords: 'Dm7 G7 Cmaj7' },
    { name: 'Blues in A', chords: 'A A D A E D A E' },
    { name: 'Minor Progression', chords: 'Am F C G' },
    { name: 'Pachelbel Canon', chords: 'C G Am Em F C F G' }
  ] as const;
}
